import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CloudTwinSharedModule } from '@cloud-twin-v2/cloud-twin-shared/cloud-twin-shared.module';
import { TCEmbeddedModeService } from '@cloud-twin-v2/cloud-twin-shared/core/embedded-mode.service';
import { TCSpinnerSplashService } from '@cloud-twin-v2/cloud-twin-shared/tc-spinner-splash.service';
import { InstanceUpdatesWorkerLoaderService } from '@cloud-twin-v2/cloud-twin-shared/workers/instance-updates-worker-loader.service';
import { TCGeneratedClientModule } from '@cloud-twin-v2/generated/generated-client.module';
import { CtMainLayoutModule } from '@cloud-twin-v2/main-layout/ct-main-layout.module';
import { SidePanelModule } from '@cloud-twin-v2/side-panel/side-panel.module';
import { StateModule } from '@cloud-twin/state/state.module';
import { HttpCacheClient } from '@common-modules/cache/http-cache/http-cache.client';
import { HttpCacheInterceptor } from '@common-modules/cache/http-cache/http-cache.interceptor';
import { HttpCacheManager } from '@common-modules/cache/http-cache/http-cache.manager';
import { TokenInterceptor } from '@common-modules/shared/auth/token.interceptor';
import { BaseCustomizableChartService } from '@common-modules/shared/charts/base-customizable-chart.service';
import { TenantInterceptor } from '@common-modules/shared/config/tenant.interceptor';
import { APP_NAVIGATIONS } from '@common-modules/shared/core/injection-tokens/app-navigations.token';
import { APP_PERMISSIONS } from '@common-modules/shared/core/injection-tokens/app-permissions.token';
import { CURRENT_ENVIRONMENT } from '@common-modules/shared/core/injection-tokens/current-environment.token';
import { GLOBAL_DISABLE_SETTINGS } from '@common-modules/shared/core/injection-tokens/global-disable-settings.token';
import { PROVIDERS_INJECTABLE_BY_KEY } from '@common-modules/shared/injectable-by-key/injectable-by-key.decorator';
import { DatesInterceptor } from '@common-modules/shared/interceptors/dates.interceptor';
import { NotificationSelectionService } from '@common-modules/shared/notifications/notification-selection.service';
import { UsersIdentityService } from '@common-modules/shared/roles/identity.service';
import { BaseCustomizableChartDataService } from '@common-modules/wlm-charts/core/customizable-chart/base-customizable-chart-data.service';
import { WLMGridModule } from '@common-modules/wlm-grid/wlm-grid.module';
import { WlmMapModule } from '@common-modules/wlm-map/wlm-map.module';
import { SpinnerInterceptor } from '@common-modules/wlm-spinner/spinner.interceptor';
import { WlmSpinnerModule } from '@common-modules/wlm-spinner/wlm-spinner.module';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { CloudTwinAppRoutingModule } from './cloud-twin-app-routing.module';
import { CloudTwinAppComponent } from './cloud-twin-app.component';
import { cloudTwinAppNavigations } from './cloud-twin-navigations';
import { cloudTwinAppPermissions } from './cloud-twin-permissions';
import { ctGlobalDisableSettings } from './cloud-twin-shared/core/attributes/ct-global-disable-settings';
import { InstanceUpdatesManagerService } from './cloud-twin-shared/workers/instance-updates-manager.service';
import { cloudTwinTranslateModuleConfigurator } from './cloud-twin-translations';
import { CTCustomizableChartDataService } from './ct-dashboards/dashboard-chart/services/ct-customizable-chart-data.service';
import { CTCustomizableChartService } from './ct-dashboards/dashboard-chart/services/ct-customizable-chart.service';
import { CtSecurityModule } from './ct-security/ct-security.module';
import { ModelVersionModule } from './model-version/model-version.module';
import { CtNavigationFilterService } from './navigation/services/ct-navigation-filter.service';
import { TelemetryModule } from './telemetry/telemetry.module';

@NgModule({
  declarations: [CloudTwinAppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ...StateModule.rootDependencies(),
    CtMainLayoutModule,
    TranslateModule.forRoot(cloudTwinTranslateModuleConfigurator),
    WlmSpinnerModule.forFeature({
      service: TCSpinnerSplashService,
    }),
    WLMGridModule,
    SidePanelModule,
    CloudTwinAppRoutingModule,
    WlmMapModule,
    TCGeneratedClientModule,
    CloudTwinSharedModule,
    ModelVersionModule,
    TelemetryModule,
    CtSecurityModule,
  ],
  providers: [
    TCEmbeddedModeService,
    NotificationSelectionService,
    InstanceUpdatesWorkerLoaderService,
    InstanceUpdatesManagerService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DatesInterceptor,
      multi: true,
    },
    HttpCacheManager,
    provideAppInitializer(() => {
      const initializerFn = (
        (cacheManager: HttpCacheManager) => () =>
          cacheManager.start(1)
      )(inject(HttpCacheManager));
      return initializerFn();
    }),
    HttpCacheClient,
    ...PROVIDERS_INJECTABLE_BY_KEY,
    {
      provide: CURRENT_ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: APP_PERMISSIONS,
      useValue: cloudTwinAppPermissions,
    },
    {
      provide: APP_NAVIGATIONS,
      useValue: cloudTwinAppNavigations,
    },
    UsersIdentityService,
    {
      provide: GLOBAL_DISABLE_SETTINGS,
      useValue: ctGlobalDisableSettings,
    },
    {
      provide: BaseCustomizableChartDataService,
      useClass: CTCustomizableChartDataService,
    },
    {
      provide: BaseCustomizableChartService,
      useClass: CTCustomizableChartService,
    },
    {
      provide: CtNavigationFilterService,
      useClass: CtNavigationFilterService,
    },
  ],
  bootstrap: [CloudTwinAppComponent],
})
export class CloudTwinAppModule {
  static injector: Injector;

  constructor(injector: Injector) {
    CloudTwinAppModule.injector = injector;
  }
}
